<template>
  <ClientOnly>
    <Title>
      {{ $t("bnpl:page_title.customer_limit") }}
    </Title>
    <CustomerLimitWelcome v-if="position === BNPL_LIMIT_FLOW_POSITION.LOGIN" />
    <div
      v-if="position !== BNPL_LIMIT_FLOW_POSITION.LOGIN"
      class="pageContainer"
    >
      <div
        v-if="loading || isLeavingIntro || isLeavingPsd2"
        class="contentContainer grow"
      >
        {{ $t(":loading...") }}
      </div>
      <template v-else>
        <IntroKyc
          v-if="
            position === BNPL_LIMIT_FLOW_POSITION.INTRO &&
            customerLimitStore.introKycRequired
          "
          :reference="String(customerLimitStore.limit.id)"
          :host-flow="KYC_HOST_FLOW.CUSTOMER_LIMIT"
        />

        <CustomerLimitQuestions
          v-if="position === BNPL_LIMIT_FLOW_POSITION.DATA"
        />
        <h1
          v-if="position === BNPL_LIMIT_FLOW_POSITION.EMAIL_VERIFICATION"
          style="
            width: 100%;
            text-align: center;
            margin: 32px 0 0 0;
            word-break: break-word;
          "
        >
          {{ $t("bnpl:customer_limit.email_verification.title") }}
        </h1>
        <EmailConfirmation
          v-if="position === BNPL_LIMIT_FLOW_POSITION.EMAIL_VERIFICATION"
          in-customer-limit
        />
        <div
          v-if="position === BNPL_LIMIT_FLOW_POSITION.PSD2_SYNC"
          class="flex-column grow"
        >
          <h1 class="h bold my-4">
            {{ $t("bnpl:limit.psd2.flow_step.name") }}
          </h1>
          <Psd2Screens
            :flow-id="customerLimitStore.flowId"
            @done="leavePsd2Forward"
          />
        </div>
        <CustomerLimitResult
          v-if="position === BNPL_LIMIT_FLOW_POSITION.RESULT"
        />
        <CustomerLimitMerchants
          v-if="position === BNPL_LIMIT_FLOW_POSITION.MERCHANT_RESULT"
        />
        <!-- The intro KYC brings its own action area. -->
        <ActionArea
          v-if="!isIntroKycStep"
          ref="actionArea"
          @prev="nextStep(true)"
          @next="nextStep"
        />
      </template>
    </div>
  </ClientOnly>
</template>

<script setup>
import { useI18n } from "vue-i18n";
import { useCountryStore } from "~~/stores/country";
import { useCustomerLimitStore } from "~~/stores/customerLimit";
import { useProfileStore } from "~~/stores/profile";
import { useSessionStore } from "~~/stores/session";
import { BNPL_LIMIT_FLOW_POSITION, KYC_HOST_FLOW } from "~~/utils/const";

definePageMeta({ layout: "payment" });

const i18n = useI18n();
const route = useRoute();
const toast = useToast();

const sessionStore = useSessionStore();
const customerLimitStore = useCustomerLimitStore();
const profileStore = useProfileStore();
const countryStore = useCountryStore();

const actionArea = ref(null);
const loading = ref(true);

const position = computed(() => {
  // return BNPL_LIMIT_FLOW_POSITION.RESULT;
  if (!sessionStore.isLoggedIn) {
    return BNPL_LIMIT_FLOW_POSITION.LOGIN;
  }

  if (customerLimitStore.limit?.flowPosition) {
    return customerLimitStore.limit.flowPosition;
  }
  return null;
});

const isIntroKycStep = computed(
  () =>
    position.value === BNPL_LIMIT_FLOW_POSITION.INTRO &&
    customerLimitStore.introKycRequired
);

useScrollTopOnChange(position);

// The explanatory intro screen is gone: what it used to say is on the login
// screen now, behind "Mi ez?". The position itself still exists server side and
// can carry an identification, so only the empty case is walked past - the same
// step the intro's own button used to take.
const isLeavingIntro = ref(false);

/**
 * The "next-step" call that follows a finished PSD2 sync only advances the
 * flow position - it does not carry the freshly computed limit, which is
 * fetched separately once the dedicated result page mounts. Without this
 * guard, this page's own RESULT branch renders in between, off a `limit`
 * that has no `expirationDate` yet, showing a false "no limit" screen for
 * however long the navigation away takes.
 */
const isLeavingPsd2 = ref(false);

watch(
  [position, loading, () => customerLimitStore.introKycRequired],
  async ([position, loading, introKycRequired]) => {
    if (loading || introKycRequired || isLeavingIntro.value) {
      return;
    }
    if (position !== BNPL_LIMIT_FLOW_POSITION.INTRO) {
      return;
    }
    isLeavingIntro.value = true;
    try {
      await customerLimitStore.nextStep(countryStore.countryId);
    } catch {
      // the customer is left on the intro position rather than on a broken
      // screen, and the next attempt is allowed to run
      console.error("Could not step past the customer limit intro");
    }
    isLeavingIntro.value = false;
  },
  { immediate: true }
);

watch(position, (newPosition) => {
  if (newPosition === BNPL_LIMIT_FLOW_POSITION.CLOSED) {
    navigateTo({ name: "financier" });
  }
});

/**
 * PSD2 is done with this customer, so the limit flow moves on. Same two steps
 * the generic action area takes at this position, which cannot reach here: it
 * is hidden while PSD2 is on screen.
 */
async function leavePsd2Forward() {
  isLeavingPsd2.value = true;
  try {
    await customerLimitStore.nextStep(countryStore.countryId);
    await customerLimitStore.waitForLimitDecision(countryStore.countryId);
    navigateTo({ name: "financier-customer-limit-result" });
  } catch (e) {
    isLeavingPsd2.value = false;
    throw e;
  }
}

/**
 * The DATA fields save themselves on blur only, so autofill or moving on straight from a field
 * can leave the customer record without them while the local store looks complete. Limit scoring
 * reads all four from that record (names for the PSD2 owner check, birth date and post code for
 * the scorecards), so persist them before stepping. A failed save is not fatal here: the backend
 * refuses to leave DATA without them anyway.
 */
async function saveProfileData() {
  try {
    await profileStore.customerUpdate({
      lastName: profileStore.lastName,
      firstName: profileStore.firstName,
      dateOfBirth: profileStore.dateOfBirth,
      postCode: profileStore.postCode,
    });
  } catch (err) {
    console.log(err);
  }
}

function showDataValidationError(field) {
  toast.add({
    message: i18n.t("bnpl:customer_limit.data.validation_error", {
      field: i18n.t(`bnpl:special_field.customer.${field}.label`),
    }),
    duration: 5000,
    type: "error",
  });
}

async function nextStep(stepBack = false) {
  switch (position.value) {
    case BNPL_LIMIT_FLOW_POSITION.PSD2_SYNC:
      navigateTo({
        name: "financier-customer-limit-result",
      });
      break;
    case BNPL_LIMIT_FLOW_POSITION.RESULT:
      if (customerLimitStore.limit.hasLateDeals) {
        navigateTo({
          name: "financier",
        });
      }
      break;
    case BNPL_LIMIT_FLOW_POSITION.DATA: {
      const error = customerLimitStore.validateDynamicData();
      if (error !== null) {
        showDataValidationError(error);
        break;
      }
      await saveProfileData();
      await customerLimitStore.nextStep(countryStore.countryId, stepBack);
      if (position.value === BNPL_LIMIT_FLOW_POSITION.DATA) {
        // The backend keeps the limit here while the customer record lacks a field that scoring
        // reads from it - resync with the record and name the field that is really missing.
        await profileStore.fetch();
        showDataValidationError(
          customerLimitStore.validateDynamicData() ?? "post_code"
        );
      }
      break;
    }
    default:
      customerLimitStore.nextStep(countryStore.countryId, stepBack);
  }
}

watch(
  [position, actionArea],
  ([position, _]) => {
    if (actionArea.value) {
      actionArea.value.reset();
      actionArea.value.isPrevHidden = true;
      console.log(position);
      switch (position) {
        // INTRO either identifies the customer, which brings its own action area,
        // or it is stepped past - neither wants the generic one on screen
        case BNPL_LIMIT_FLOW_POSITION.INTRO:
        case BNPL_LIMIT_FLOW_POSITION.LOGIN:
        case BNPL_LIMIT_FLOW_POSITION.EMAIL_VERIFICATION:
        case BNPL_LIMIT_FLOW_POSITION.PSD2_SYNC:
          actionArea.value.isHidden = true;
          break;
        case BNPL_LIMIT_FLOW_POSITION.RESULT:
          if (customerLimitStore.limit.hasLateDeals) {
            actionArea.value.nextLabel = i18n.t(
              "bnpl:customer_limit.result.back_to_selfcare"
            );
          }
          break;
      }
    }
  },
  { immediate: true }
);

watch(
  () => sessionStore.isLoggedIn,
  async (isLoggedIn) => {
    if (isLoggedIn) {
      try {
        await customerLimitStore.fetchIsCustomerLimitEnabled(
          route.params.financier
        );
        if (!customerLimitStore.isCustomerLimitEnabled) {
          navigateTo({
            name: "financier",
          });
        }
      } catch {
        console.error("Could not check customer limit settings");
      }

      try {
        await customerLimitStore.getMyActiveLimit(countryStore.countryId);
        navigateTo({
          name: "financier-customer-limit-result",
        });
      } catch {
        try {
          try {
            await customerLimitStore.getMyInProgressLimit(
              countryStore.countryId
            );
          } catch {
            // A limit closed by a rejected identification cannot be reopened, and create() is the
            // only thing that says so - it answers 409. Whatever the reason, the customer must not
            // be left staring at the loading screen.
            await customerLimitStore.create(countryStore.countryId);
          }
          await customerLimitStore.fetchIntroKyc(countryStore.countryId);
          // The PSD2 screens need this to ask how long an earlier sync stays
          // valid, and they are mounted as soon as loading clears. Its own
          // failure must not stop the flow.
          try {
            await customerLimitStore.fetchFlowId(countryStore.countryId);
          } catch {
            console.error("Could not resolve the customer limit flow id");
          }
        } catch (err) {
          toast.add({
            message:
              err?.statusCode === 409
                ? i18n.t("bnpl:intro_kyc.rejected.description")
                : i18n.t("bnpl:customer_limit.create.error"),
            duration: 8000,
            type: "error",
          });
          navigateTo({ name: "financier" });
        } finally {
          loading.value = false;
        }
      }
    }
  },
  { immediate: true }
);

onMounted(() => {
  customerLimitStore.merchantId = route.query.merchantId;
  navigateTo({ name: "financier-customer-limit", replace: true });
});
</script>

<style scoped></style>
